import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAppointment, sendMobileCheckInEmail } from '../../API/GoMoto';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentState, useMaskEmail } from '../../context/Appointment/appointment';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { useLocale, useLocalization } from '../../context/Localization/localization';
import { AppointmentQRCode } from '../../controls/AppointmentQRCode/AppointmentQRCode';
import { GMUppercase } from '../../controls/Case';
import { CTAButton } from '../../controls/ButtonBase';
import { IconButton } from '../../controls/IconButton/IconButton';
import { Page } from '../Layout/Page/Page';
import { ScrollableBody } from '../Layout/ScrollableBody';
import { maskValue } from '../../helpers/maskValue';
import css from './SaveSession.module.scss';
import { AppRoutePaths } from '../../helpers/Routes';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMSizedIcon } from '../../controls/Icon';
import { useSavedSessionSave } from '../../context/SavedSession/Hooks';

export const SaveSession = React.memo(() => {
    const Localization = useLocalization();
    const { saveSession } = useSavedSessionSave();
    const navigate = useNavigate();
    const [saved, setSaved] = React.useState(false);
    const { kioskType } = useKioskState();
    const appointment = useAppointmentState();
    const appointmentDispatch = useAppointmentDispatch();
    const hasNoKiosk: boolean = kioskType === 'mobile' || !kioskType;

    return (
        <Page
            customHeaderProps={{
                title: Localization.SaveSession,
                rightIcons: [
                    <IconButton
                        key={'save-session-close'}
                        sizedIcon={
                            <GMSizedIcon
                                iconType={'Close'}
                                iconSize={'medium'}
                                ariaLabel={Localization.CloseIcon}
                                title={Localization.ClickToCloseWindow}
                            />
                        }
                        onClick={async () => {
                            if (appointment) {
                                const appt = await getAppointment(appointment.key);
                                if (appt) {
                                    appointmentDispatch({
                                        type: AppointmentDispatchTypes.Update,
                                        payload: appt,
                                    });
                                }
                            }
                            navigate(-1);
                        }}
                        ariaLabel={Localization.ClickToCloseWindow}
                        id={Localization.ClickToCloseWindow}
                    />,
                ],
            }}
            showKioskBanner={kioskType !== 'outdoor'}
        >
            {!saved ? (
                <>
                    <ScrollableBody>
                        <h2 className={css.savedSessionTitle}>
                            {kioskType === 'outdoor' ? Localization.SaveSessionAndContinueOutdoorKiosk : Localization.SaveSessionAndContinue}
                        </h2>
                    </ScrollableBody>
                    <GMButtonGroup
                        buttons={[
                            (
                                <CTAButton
                                    styleType={'primary'}
                                    onClick={async () => {
                                        await saveSession();
                                        setSaved(true);
                                    }}
                                    title={Localization.ClickToSaveProgress}
                                    label={Localization.YesSaveMySpot}
                                    ariaLabel={Localization.ClickToSaveProgress}
                                    id={Localization.ClickToSaveProgress}
                                />
                            ),
                            (
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={() => navigate(-1)}
                                    title={Localization.ClickToResumeOnMobile}
                                    label={Localization.NoContinueHere}
                                    id={Localization.ClickToResumeOnMobile}
                                    ariaLabel={Localization.ClickToResumeOnMobile}
                                />
                            ),
                        ]}
                    />
                </>
            ) : (
                <ScrollableBody
                    proceedButtons={
                        <GMButtonGroup
                            buttons={[
                                (
                                    <CTAButton
                                        styleType={'primary'}
                                        onClick={async () => {
                                            navigate(`${AppRoutePaths.ImHere}?apptkey=${appointment.key}`);
                                        }}
                                        label={Localization.ImHere}
                                        id={Localization.ImHere}
                                        ariaLabel={Localization.ImHere}
                                    />
                                ),
                            ]}
                        />
                    }
                >
                    <h2 className={css.savedSessionTitle}>{Localization.SessionSaved}</h2>
                    {kioskType === 'outdoor' || hasNoKiosk ? <SessionSavedOutdoorKiosk /> : <SessionSaved />}
                </ScrollableBody>
            )}
        </Page>
    );
});

const SessionSaved: React.FC = () => {
    const Localization = useLocalization();
    const { email } = useAppointmentState();
    const maskEmail = useMaskEmail();
    const {
        state: { from },
    }: { state: { from: string; }; } = useLocation();

    return (
        <>
            <p>{Localization.formatString(Localization.PleaseTakeThisQRCode, maskEmail ? maskValue(email) : <a href={'mailto:' + email}>{email}</a>)}</p>
            <AppointmentQRCode currentUrl={from} />
            <p>
                {Localization.formatString(
                    Localization.SaveSessionImHere,
                    <b>
                        <GMUppercase>{Localization.ImHere}</GMUppercase>
                    </b>,
                )}
            </p>
        </>
    );
};

const SessionSavedOutdoorKiosk: React.FC = () => {
    const Localization = useLocalization();
    const appointment = useAppointmentState();
    const maskEmail = useMaskEmail();
    const {
        dealer: { key: dealerKey },
    } = useKioskState();
    const locale = useLocale();
    const [emailSent, setEmailSent] = useState<boolean>(false);
    const {
        state: { from },
    }: { state: { from: string; }; } = useLocation();

    React.useEffect(() => {
        if (!emailSent) {
            sendMobileCheckInEmail(appointment.key, dealerKey, locale, false, from, undefined, undefined, undefined, appointment.phoneTypeEnum)
                .then(
                    (_qrCode: string) => {
                        setEmailSent(true);
                    },
                );
        }
    }, [appointment, dealerKey, locale, emailSent, from]);

    return (
        <>
            <p>
                {Localization.formatString(
                    Localization.SaveSessionEmailConfirmation,
                    maskEmail ? maskValue(appointment.email) : <a href={'mailto:' + appointment.email}>{appointment.email}</a>,
                )}
            </p>
            <p>
                {Localization.formatString(
                    Localization.SaveSessionImHere,
                    <b>
                        <GMUppercase>{Localization.ImHere}</GMUppercase>
                    </b>,
                )}
            </p>
        </>
    );
};
